import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Grid, Theme, withStyles, WithStyles } from '@material-ui/core';
import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon, WatchLater as PendingIcon } from '@material-ui/icons';
import { get } from 'lodash';

import { MessageTemplate } from 'core';
import { IconCalendarClock, IconEditCalendar } from 'icons';

import SvgIconWrapper from './SvgIconWrapper';
import Typography from './Typography';

const messages = defineMessages({
  confirmedTitle: {
    id: 'components.v2.AppointmentStatusInfo.confirmedTitle',
    defaultMessage: 'Confirmed Appointment',
  },
  confirmedSubtitle: {
    id: 'components.v2.AppointmentStatusInfo.confirmedSubtitle',
    defaultMessage: 'Success! Your appointment has been confirmed. We’ll see you soon.',
  },
  unconfirmedTitle: {
    id: 'components.v2.AppointmentStatusInfo.unconfirmedTitle',
    defaultMessage: 'Confirm Appointment',
  },
  unconfirmedSubtitle: {
    id: 'components.v2.AppointmentStatusInfo.unconfirmedSubtitle',
    defaultMessage: 'Please confirm or cancel your appointment.',
  },
  canceledTitle: {
    id: 'components.v2.AppointmentStatusInfo.canceledTitle',
    defaultMessage: 'Canceled Appointment',
  },
  canceledSubtitle: {
    id: 'components.v2.AppointmentStatusInfo.canceledSubtitle',
    defaultMessage: 'Your appointment has been canceled. You can always book a new appointment!',
  },
  submitRequestTitle: {
    id: 'components.v2.AppointmentStatusInfo.submitRequestTitle',
    defaultMessage: 'Request Appointment',
  },
  submitRequestSubtitle: {
    id: 'components.v2.AppointmentStatusInfo.submitRequestSubtitle',
    defaultMessage: 'Please review and submit your new appointment request.',
  },
  submitRequestSubtitleLock: {
    id: 'components.v2.AppointmentStatusInfo.submitRequestSubtitleLock',
    defaultMessage:
      'This spot is reserved for the next {lockTimeout} minutes. Please, complete this form to book your appointment',
  },
  pendingApptTitle: {
    id: 'components.v2.AppointmentStatusInfo.pendingApptTitle',
    defaultMessage: 'Pending Appointment',
  },
  pendingApptSubtitle: {
    id: 'components.v2.AppointmentStatusInfo.pendingApptSubtitle',
    defaultMessage: "Got it! We'll let you know when your appointment is confirmed",
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    container: {
      padding: `${unit * 4}px ${unit * 3}px ${unit * 2.5}px`,
    },
    svgIcon: {
      fontSize: unit * 4.5,
      marginBottom: unit * 1.5,
    },
    marginBottom: {
      marginBottom: unit / 2,
    },
  });

export interface AppointmentStatusInfoProps extends WithStyles<typeof styles> {
  appointmentStatus: string;
  compact?: boolean;
  lockTimeout?: number;
}

const AppointmentStatusInfo: React.FC<AppointmentStatusInfoProps> = ({
  appointmentStatus,
  classes,
  compact,
  lockTimeout,
}) => {
  const { formatMessage } = useIntl();

  function getStatusObject(status: string) {
    switch (status) {
      case 'confirmed':
        return {
          icon: <SvgIconWrapper Icon={CheckCircleIcon} color="success" className={classes.svgIcon} />,
          title: formatMessage(messages.confirmedTitle),
          subtitle: formatMessage(messages.confirmedSubtitle),
        };
      case 'unconfirmed':
        return {
          icon: <SvgIconWrapper Icon={IconCalendarClock} color="supportSteel" className={classes.svgIcon} />,
          title: formatMessage(messages.unconfirmedTitle),
          subtitle: formatMessage(messages.unconfirmedSubtitle),
        };
      case 'cancelled':
        return {
          icon: <SvgIconWrapper Icon={CancelIcon} color="danger" className={classes.svgIcon} />,
          title: formatMessage(messages.canceledTitle),
          subtitle: formatMessage(messages.canceledSubtitle),
        };
      case 'available':
        return {
          icon: <SvgIconWrapper Icon={IconEditCalendar} color="supportSteel" className={classes.svgIcon} />,
          title: formatMessage(messages.submitRequestTitle),
          subtitle: lockTimeout
            ? formatMessage(messages.submitRequestSubtitleLock, { lockTimeout })
            : formatMessage(messages.submitRequestSubtitle),
        };
      case 'booked-pending':
        return {
          icon: <SvgIconWrapper Icon={PendingIcon} color="secondary" className={classes.svgIcon} />,
          title: formatMessage(messages.pendingApptTitle),
          subtitle: formatMessage(messages.pendingApptSubtitle),
        };
      default:
        break;
    }
  }

  const statusObject = getStatusObject(appointmentStatus);

  return (
    <Grid item container xs={12} className={classes.container}>
      <Grid item xs={12} container direction="column" alignItems="center" justify="center">
        {get(statusObject, 'icon')}
        <Typography variant="h2W600F20" color="supportSlate" align="center" className={classes.marginBottom}>
          {get(statusObject, 'title', '')}
        </Typography>
        {!compact && (
          <>
            <Typography variant="supportW500F14" align="center">
              {get(statusObject, 'subtitle', '')}
            </Typography>
            <Typography variant="supportW500F14" align="center">
              <MessageTemplate id="messages.web.patientAppointmentConfirmationDetails" />
            </Typography>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(AppointmentStatusInfo);
